import { useEffect } from "react";
import { useStore } from "zustand";
import { marketStore } from "entities/market";
import { generalSocketStore } from "services/general.socket";
import { ioRTStore } from "services/rt.socket";

interface IProps {
  asset_id?: string;
  /** Код биржи, как в usePrice (например, "AMX" или "MOEX"). */
  exchangeCode?: string;
  symbol?: string;
  isAMX: boolean;
  /** Показывается ли стакан вообще (для не-AMX страниц можно передавать true). */
  enabled: boolean;
}

/**
 * Источник стакана (marketStore.assetDepth) в зависимости от биржи:
 *  - AMX   → realtime из Market Data socket (rt.socket, событие 'orderbook'),
 *            подписку на тикер уже держит usePrice: у iMix глубина приезжает
 *            внутри той же котировки.
 *  - MOEX  → realtime оттуда же, но книгу нужно подписать отдельно
 *            ('subscribe-book'): у moex-fast-md это самостоятельный канал, и
 *            тянуть его для каждой бумаги в списке было бы расточительно.
 *  - иначе → прежний путь через backend general socket
 *            ('subscribe-orderbook' / 'update-orderbook').
 *
 * Первичный стакан грузится так же, как и раньше (REST/начальное состояние
 * страницы); хук только подменяет источник live-обновлений.
 */
const useOrderbook = ({
  asset_id,
  exchangeCode,
  symbol,
  isAMX,
  enabled,
}: IProps) => {
  const ticker = `${exchangeCode || ""}~${symbol || ""}`;
  const isMOEX = exchangeCode === "MOEX";
  const fromMarketData = isAMX || isMOEX;

  const rtOrderbook = useStore(ioRTStore, (s) => s.orderbooks[ticker]);
  const rtConnected = useStore(ioRTStore, (s) => s.connected);
  const subscribeBook = useStore(ioRTStore, (s) => s.subscribeBook);
  const unsubscribeBook = useStore(ioRTStore, (s) => s.unsubscribeBook);
  const { connected, socket } = useStore(generalSocketStore);

  // ─── ВАРИАНТ A (Market Data socket) — ВКЛЮЧЁН ───
  // AMX и MOEX: зеркалим realtime-стакан из Market Data в marketStore.assetDepth.
  // Не завязываемся на `enabled` (видимость стакана контролируется отдельно
  // при рендере) — если данные по тикеру пришли, отражаем их.
  useEffect(() => {
    if (!fromMarketData || !rtOrderbook) return;
    marketStore.setState({ assetDepth: rtOrderbook });
  }, [fromMarketData, rtOrderbook]);

  // Книга MOEX живёт ровно столько, сколько открыта страница бумаги.
  // Пересоздаётся и после реконнекта: подписки сокет не помнит.
  useEffect(() => {
    if (!isMOEX || !enabled || !rtConnected || !symbol) return;
    subscribeBook(ticker);
    return () => {
      unsubscribeBook(ticker);
    };
  }, [isMOEX, enabled, rtConnected, ticker, symbol]);

  // ─── ВАРИАНТ B (backend general socket для AMX) — ОТКЛЮЧЁН ───
  // Не-AMX идут через backend general socket всегда; AMX — только когда включён
  // ВАРИАНТ B (backend). Чтобы переключить AMX на backend: закомментировать
  // ВАРИАНТ A выше, убрать `isAMX` из условия ниже (взять строку ВАРИАНТА B) и
  // раскомментировать AMX-ветку в backend-кроне updateOrderBooks.
  useEffect(() => {
    // ВАРИАНТ A (Market Data, включён): AMX и MOEX исключены из backend-ветки:
    if (!enabled || fromMarketData || !connected || !asset_id) {
    // ВАРИАНТ B (backend): backend обслуживает и AMX:
    // if (!enabled || !connected || !asset_id) {
      return;
    }
    const onUpdate = (data: any) => {
      const { asset_id: as_id, orderbook } = data;
      if (as_id == +asset_id) {
        marketStore.setState({ assetDepth: orderbook });
      }
    };
    socket.on("update-orderbook", onUpdate);
    socket.emit("subscribe-orderbook", { asset_id });

    return () => {
      socket
        .emit("unsubscribe-orderbook", { asset_id })
        .off("update-orderbook", onUpdate);
    };
  }, [enabled, fromMarketData, asset_id, connected]);
};

export default useOrderbook;
